<template>
  <v-form ref="form" v-model="valid" lazy-validation class="pa-2">
    <v-row dense>
      <!-- Operator name -->
      <v-col sm="12" md="12">
        <v-text-field
          v-model="formData.label"
          :rules="nameRules"
          :label="$t('labels.name')"
          variant="outlined"
          density="compact"
          required
        ></v-text-field>
      </v-col>

      <!-- Accepted file type -->
      <v-col sm="12" md="4">
        <v-select
          v-model="formData.fileType"
          :items="fileTypes"
          item-title="title"
          item-value="value"
          :label="$t('labels.file-type')"
          variant="outlined"
          density="compact"
          required
        ></v-select>
      </v-col>

      <!-- File upload -->
      <v-col sm="12" md="8">
        <v-file-input
          v-model="selectedFile"
          :accept="acceptedFileTypes"
          :label="$t('labels.select-file')"
          variant="outlined"
          density="compact"
          prepend-icon=""
          prepend-inner-icon="mdi-file-upload"
          :loading="uploading"
          :disabled="uploading"
          @update:modelValue="handleFileChange"
          clearable
          show-size
          :hint="$t('strings.file-max-size-hint')"
          persistent-hint
          persistent-clear
        ></v-file-input>
      </v-col>

      <!-- Status message -->
      <v-col sm="12" v-if="statusMessage">
        <v-alert
          :type="statusType"
          density="compact"
          variant="tonal"
          class="mb-4"
        >
          {{ statusMessage }}
        </v-alert>
      </v-col>

      <!-- Use like constant -->
      <v-col cols="auto" class="d-flex align-center">
        <v-checkbox
          v-model="formData.isConstant"
          :label="$t('strings.use-like-constant')"
          color="secondary"
          :hide-details="true"
        ></v-checkbox>
        <v-tooltip location="top" max-width="320">
          <template v-slot:activator="{ props }">
            <v-icon
              v-bind="props"
              icon="mdi-information-outline"
              size="small"
              class="ml-1"
              color="info"
            ></v-icon>
          </template>
          <div>
            <div class="font-weight-bold mb-1">
              {{ $t("strings.use-like-constant-tooltip-title") }}
            </div>
            <div class="mb-2">
              {{ $t("strings.use-like-constant-tooltip-enabled") }}
            </div>
            <div class="mb-2">
              {{ $t("strings.use-like-constant-tooltip-disabled") }}
            </div>
            <div>{{ $t("strings.use-like-constant-tooltip-fallback") }}</div>
          </div>
        </v-tooltip>
      </v-col>

      <!-- Value returned by the API (non-editable) -->
      <v-col sm="12" md="6" v-if="paramValues.property" class="d-none">
        <v-text-field
          v-model="paramValues.property"
          :label="$t('labels.api-response')"
          variant="outlined"
          density="compact"
          readonly
          prepend-inner-icon="mdi-check-circle"
          persistent-hint
        ></v-text-field>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { inject, provide, ref, reactive, watch, computed } from "vue";
import { useI18n } from "vue-i18n";
import axios from "axios";
import { retrievesParameters } from "@/mixins/common-utils.js";

export default {
  name: "OperatorInputFile",
  props: ["target", "data", "operatorInputs"],
  emits: ["updateFormData"],
  setup(props, { emit }) {
    const valid = inject("valid");
    const realm = inject("tenant");
    const config = inject("config");
    const api = config.api;
    const { t } = useI18n();
    // Creates a deep copy, unlinked of target.userData
    const formData = reactive(
      JSON.parse(JSON.stringify(props.target.userData)) || {},
    );

    // Ensure parameters array exists
    if (!formData.parameters) formData.parameters = [];
    // Set default values if not present
    if (!formData.label) formData.label = props.target.id; //|| "Import File";
    if (!formData.fileType) formData.fileType = "all";
    if (!formData.fileName) formData.fileName = "";
    if (!formData.fileSize) formData.fileSize = 0;
    if (formData.isConstant === undefined) formData.isConstant = false;

    provide("formData", formData);

    // Retrieves Operator parameter or create it if not exists
    let paramValues = retrievesParameters("refId", formData.parameters);

    if (!paramValues) {
      // Create parameter if not found
      paramValues = {
        name: "refId",
        property: "",
      };
      formData.parameters.push(paramValues);
    } else {
      // Ensure property exists
      if (!paramValues.property) {
        paramValues.property = "";
      }
    }

    // Supported file types - retrieved from configuration or fallback
    const importFileTypeConfig = config.importFileType;

    // Dynamically builds the file types array from configuration
    const fileTypes = ref(
      Object.keys(importFileTypeConfig).map((key) => ({
        title: key.charAt(0).toUpperCase() + key.slice(1),
        value: key,
      })),
    );

    // Add the "All" option
    fileTypes.value.push({ title: t("labels.all"), value: "all" });

    // Selected file
    const selectedFile = ref(null);
    const uploading = ref(false);
    const statusMessage = ref("");
    const statusType = ref("info");

    // Restore the file if fileName exists
    if (formData.fileName && formData.fileSize) {
      // Create a Blob of the correct size
      const blob = new Blob([new ArrayBuffer(formData.fileSize)], {
        type: "application/octet-stream",
      });
      const file = new File([blob], formData.fileName, {
        type: "application/octet-stream",
      });
      selectedFile.value = [file];
    }

    // Validation rules
    const nameRules = ref([(v) => !!v || t("strings.name-is-required")]);

    // Accepted file types based on selection
    const acceptedFileTypes = computed(() => {
      if (formData.fileType === "all") {
        // Return all file types from configuration
        return Object.values(importFileTypeConfig).join(",");
      }
      // Return the specific type from configuration, ensuring correct format
      const fileType = importFileTypeConfig[formData.fileType];
      if (!fileType) return "";

      // Ensure extensions start with a dot
      return fileType
        .split(",")
        .map((ext) =>
          ext.trim().startsWith(".") ? ext.trim() : "." + ext.trim(),
        )
        .join(",");
    });

    const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1 MB

    // File change handler
    const handleFileChange = async (file) => {
      if (!file) {
        formData.fileName = "";
        formData.fileSize = 0;
        paramValues.property = "";
        statusMessage.value = "";
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        statusMessage.value = t("strings.file-exceeds-max-size");
        statusType.value = "error";
        selectedFile.value = null;
        formData.fileName = "";
        formData.fileSize = 0;
        paramValues.property = "";
        return;
      }

      formData.fileName = file.name;
      formData.fileSize = file.size;

      // File upload and API call
      await uploadFile(file);
    };

    // Function to upload the file and call the API
    const uploadFile = async (file) => {
      uploading.value = true;
      statusMessage.value = t("strings.uploading-file");
      statusType.value = "info";

      try {
        // Create FormData
        const formDataToSend = new FormData();
        formDataToSend.append("file", file);

        // Build API URL with tenantId
        const apiUrl = `${api}/${realm}/operator/importFile`;

        // API call with axios
        const response = await axios.post(apiUrl, formDataToSend, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        });

        // Check if the response indicates success
        if (response.data && response.data.success) {
          // Save the refId in the parameter property
          paramValues.property = response.data.refId || "";
          statusMessage.value = t("strings.file-uploaded-successfully");
          statusType.value = "success";
        } else {
          // If success is false, consider the upload as failed
          throw new Error(response.data.message || t("strings.upload-failed"));
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        statusMessage.value =
          t("strings.error-uploading-file") + ": " + error.message;
        statusType.value = "error";
        paramValues.property = "";
      } finally {
        uploading.value = false;
      }
    };

    // Watch to emit form data
    watch(
      formData,
      (newFormData) => {
        emit("updateFormData", newFormData);
      },
      { deep: true, immediate: true },
    );

    return {
      valid,
      formData,
      paramValues,
      fileTypes,
      nameRules,
      selectedFile,
      uploading,
      statusMessage,
      statusType,
      acceptedFileTypes,
      handleFileChange,
    };
  },
};
</script>

<style scoped></style>
